import { CircularProgress, } from "@mui/material";
import { closeDrawer, DrawerStateKinds, useDrawerState, useIsLeftBarExpanded } from "../../features/Drawer/drawerSlice";
import TripLibraryDrawer from "./TripLibraryDrawer";
import TripInputDrawer from "./TripInputDrawer";
import { RiskViewerKind, setWilmaState } from "../../features/riskViewer/riskViewerSlice";
import { ZIndexLevels } from "../risk/geoUtils";
import RouteOverviewDrawer from "../metric/RouteOverviewDrawer";
import { useIsMobile } from "../../features/pageWidth/pageWidthSlice";
import { Dispatch } from "react";
import { AnyAction } from "@reduxjs/toolkit";
import MultistartSelectionDrawer from "../MultistartSelectionDrawer/MultistartSelectionDrawer";

/**
 * Handles closing of the drawer.
 * If the risk viewer state is 'selectionState', dispatches setWilmaState action.
 * Always dispatches closeDrawer action.
 */
export const handleDrawerClose = ( riskViewerStateKind: string, dispatch: Dispatch<AnyAction> ) =>
{
    if ( riskViewerStateKind === RiskViewerKind.SELECTION_STATE )
    {
        dispatch( setWilmaState() );
    }
    dispatch( closeDrawer() );
};

/**
 * Returns the width of the trip drawer based on the device type.
 * @param {boolean} isMobile - Whether the device is mobile.
 * @returns {string} - The width of the trip drawer.
 */
export function getTripDrawerWidth( isMobile: boolean ): string
{
    return isMobile ? "100%" : "400px";
}

/**
 * Component rendering the trip drawer with the appropriate content based on the drawer state.
 * @returns {JSX.Element} - The trip drawer component.
 */
export default function TripDrawer(): JSX.Element
{
    const { kind: drawerStateKind } = useDrawerState();
    const isMobile = useIsMobile();
    const isExpanded = useIsLeftBarExpanded();

    const getDrawerContent = (): JSX.Element | null =>
    {
        switch ( drawerStateKind )
        {
            case DrawerStateKinds.TripLibrary:
                return <TripLibraryDrawer/>;
            case DrawerStateKinds.TripInput:
            case DrawerStateKinds.Edit:
                return <TripInputDrawer/>;
            case DrawerStateKinds.TripResults:
                return <RouteOverviewDrawer/>;
            case DrawerStateKinds.MultistartSelection:
                return <MultistartSelectionDrawer/>;
            default:
                return null;
        }
    };

    if ( !isExpanded ) return <div/>;

    return (
        <div
            style = {{
                position: "fixed",
                top: 0,
                left: 0,
                width: getTripDrawerWidth( isMobile ),
                height: "100dvh",
                backgroundColor: "#fff",
                zIndex: ZIndexLevels.TopUIElement,
                overflowY: "auto",
                boxShadow: "0px 0px 10px rgba(0,0,0,0.2)",
                transition: "transform 0.3s ease-in-out",
                display: "flex",
                flexDirection: "column",
            }}
        >
            {getDrawerContent() || <CircularProgress/>}
        </div>
    );
}
